/**
 * Learning goals ("Lernziele") are plain groups on the cards, so a sub-set is
 * only a filter over a set. Nothing here touches the DOM or storage.
 */
import type { Card, VocabSet } from './types';
import { cardsProgress } from './leitner';

export type CardGroup = {
  /** Group name, empty for the cards that come before the first "==" line. */
  name: string;
  cards: Card[];
  /** Average box position, 0..1, same scale as `setProgress`. */
  progress: number;
};

/** Groups in the order in which they first appear in the set. */
export function listGroups(set: VocabSet): CardGroup[] {
  const byName = new Map<string, Card[]>();

  for (const card of set.cards) {
    const cards = byName.get(card.group);
    if (cards) cards.push(card);
    else byName.set(card.group, [card]);
  }

  return Array.from(byName, ([name, cards]) => ({ name, cards, progress: cardsProgress(cards) }));
}

/** True as soon as at least one card carries a named learning goal. */
export function hasGroups(set: VocabSet): boolean {
  return set.cards.some((card) => card.group !== '');
}

/** All cards of one learning goal; `null` means the whole set. */
export function cardsOfGroup(set: VocabSet, group: string | null): Card[] {
  if (group === null) return set.cards;
  return set.cards.filter((card) => card.group === group);
}

/** Falls back to the whole set when the stored group no longer exists. */
export function resolveGroup(set: VocabSet, group: string | null): string | null {
  if (group === null) return null;
  return set.cards.some((card) => card.group === group) ? group : null;
}
